import { api, apiConfig } from "./api";
import type { AnalyticsOverview } from "./types/analytics";

// ── T-ALIGN-01 · mock 数据（VITE_USE_MOCK=true 时使用，无需后端）
const MOCK_OVERVIEW: AnalyticsOverview = {
  ai_suggestions: [
    {
      id: "s1",
      icon_kind: "price_optimize",
      title_cn: "黄石南门 1日游 价格可提升 8%",
      summary_cn: "Viator 同类产品均价 $189，当前定价 $175，建议小幅上调测试转化率",
      confidence: 87,
      generated_at: "2026-05-29 08:30",
    },
    {
      id: "s2",
      icon_kind: "inventory_warn",
      title_cn: "7月4日周末库存告急",
      summary_cn: "黄石南线 7/4–7/6 车辆利用率 94%，建议提前协调备用车辆",
      confidence: 92,
      generated_at: "2026-05-29 08:30",
    },
    {
      id: "s3",
      icon_kind: "customer_insight",
      title_cn: "复购客户偏好双园产品",
      summary_cn: "近 30 天 2 次以上预订客户中 68% 选择大提顿+黄石组合，建议加强捆绑促销",
      confidence: 75,
      generated_at: "2026-05-29 08:30",
    },
    {
      id: "s4",
      icon_kind: "ota_alert",
      title_cn: "GYG 渠道 3 条订单状态未同步",
      summary_cn: "API 上次同步 2h 前，建议手动触发一次同步或检查 API Token",
      confidence: 95,
      generated_at: "2026-05-29 08:30",
    },
  ],
  kpis: [
    { id: "k1", label_cn: "本月订单", value: "247", trend: "up", trend_value: "+18%", degraded: false },
    { id: "k2", label_cn: "本月销售额", value: "$43,280", trend: "up", trend_value: "+22%", degraded: false },
    { id: "k3", label_cn: "客单价", value: "$175", trend: "up", trend_value: "+3%", degraded: false },
    { id: "k4", label_cn: "车辆利用率", value: "78%", trend: "flat", trend_value: "±0%", degraded: false },
    { id: "k5", label_cn: "客户复购率", value: "32%", trend: "up", trend_value: "+5%", degraded: false },
    { id: "k6", label_cn: "投诉率", value: "0.8%", trend: "down", trend_value: "-0.2%", degraded: false },
  ],
  order_source_dist: [
    { label_cn: "Viator", source: "viator", pct: 42, count: 104 },
    { label_cn: "GetYourGuide", source: "gyg", pct: 28, count: 69 },
    { label_cn: "携程", source: "ctrip", pct: 15, count: 37 },
    { label_cn: "直客", source: "direct", pct: 10, count: 25 },
    { label_cn: "同业 B2B", source: "b2b", pct: 5, count: 12 },
  ],
  monthly_sales_trend: [
    { month: "2026-01", label_cn: "1月", revenue_cents: 890000, orders: 45 },
    { month: "2026-02", label_cn: "2月", revenue_cents: 720000, orders: 36 },
    { month: "2026-03", label_cn: "3月", revenue_cents: 1340000, orders: 68 },
    { month: "2026-04", label_cn: "4月", revenue_cents: 2180000, orders: 112 },
    { month: "2026-05", label_cn: "5月", revenue_cents: 4328000, orders: 247 },
  ],
};

export type { AnalyticsOverview } from "./types/analytics";
export type {
  AiSuggestion,
  KpiTile,
  SourceDist,
  MonthlyTrend,
  AiSuggestionIconKind,
  KpiTrend,
} from "./types/analytics";

export interface AnalyticsDashboard {
  orders_today: number;
  orders_today_delta: number;
  gmv_today_cents: number;
  gmv_delta_pct: number;
  confirmed_today: number;
  confirm_rate_pct: number;
  pending_confirm: number;
  pending_voucher: number;
  alert_count: number;
  team: { op_on: number; op_total: number; driver_on: number; driver_total: number; vehicle_active: number; vehicle_total: number };
  today_tours: { order_no: string; customer: string; pax: number; status: string }[];
  handover: { pending: number; processed_today: number; avg_process_min: number; ranking: { name: string; count: number; avg_min: number }[] };
  inventory_health: { resource: string; d0_pct: number; d1_pct: number; d2_pct: number }[];
  channel_distribution: { channel: string; orders: number; pct: number }[];
  trend: { date: string; orders: number; gmv_cents: number; avg_cents: number }[];
  alerts: { level: string; title: string; source: string; link: string }[];
}

export interface DeepAnalysis {
  period: string;
  gmv_month_cents: number;
  gmv_mom_pct: number;
  gmv_yoy_pct: number;
  orders_month: number;
  orders_mom_delta: number;
  new_customers_month: number;
  repeat_rate_pct: number;
  nps: number;
  pl_summary: {
    revenue_cents: number;
    cost_cents: number;
    gross_profit_cents: number;
    operating_expenses_cents: number | null;
    net_profit_cents: number | null;
    _masked?: boolean;
  };
  monthly_trend: { month: string; revenue_cents: number; orders: number }[];
  channel_share: { channel: string; pct: number; prev_pct: number }[];
  top_products: { product: string; orders: number; margin_pct: number; net_cents: number }[];
  yoy_growth: { channel: string; gmv_yoy_pct: number; orders_yoy_pct: number }[];
  inventory_util: { resource: string; util_pct: number }[];
  customer_health: { tier: string; pct: number }[];
  forecast: { next_gmv_cents: number; band_cents: number; mom_pct: number; basis: string; seasonality: string; season_calendar: { period: string; label: string; impact: string }[] };
  alerts_by_type: { type: string; count: number; severity: string; link: string; hint: string }[];
  top5_products: { product: string; orders: number; margin_pct: number; net_cents: number }[];
}

export const analyticsApi = {
  dashboard: () => api<AnalyticsDashboard>("/api/v1/analytics/dashboard"),
  deep: () => api<DeepAnalysis>("/api/v1/analytics/deep"),
  /** T-ALIGN-01 · 运营总览三段数据（AI 建议 / KPI / 趋势） */
  getOverview: () =>
    apiConfig.useMock
      ? Promise.resolve(MOCK_OVERVIEW)
      : api<AnalyticsOverview>("/api/v1/analytics:overview"),
};
